import React, {Fragment, memo} from "react";
import PropTypes from "prop-types";
import {isEmpty} from "lodash";
import {useIsMobileOrTablet} from "../../hooks/useIsMobileOrTablet";
import {useSortData} from "../../hooks/useSorting";
import SortCreator from "../../components/Forms/components/SortCreator";
import SearchCreator from "../Forms/components/SearchCreator";
import Table from "../Table/Table";
import Pagination from "../Table/components/Pagination";
import CardList from "./CardList";
import {useHandleSort} from "./hooks/useHandleSort";
import NoDataFound from "./NoData";

/** This component will create table with search query component.
 *  This component is created only to work with desktop screen.
 */

const ListView = (props) => {
  const {
    handleSearch = () => {},
    listHeaders = [],
    listItems = [],
    noDataText,
    searchOptions = [],
    searchPlaceHolder = "Filter content",
    totalCount = 0,
    withActions = false,
    withPagination = false,
    withSearchQuery = false,
    sortableFields = [],
    ...rest
  } = props;

  const {isMobileOrTablet} = useIsMobileOrTablet();
  const {handleSortClick, handleSortSelect, sortColumn, sortDirection} = useHandleSort();
  const sortedData = useSortData(listItems, sortColumn, sortDirection, sortableFields);

  return (
    <Fragment>
      {withSearchQuery && (
        <div
          style={{display: "flex", justifyContent: "flex-end"}}
          data-cy={"search-query"}
        >
          <div
            style={{
              marginBottom: "10px",
              marginRight: "-1rem",
              width: isMobileOrTablet ? "calc(100% + 1rem)" : "50%",
            }}
          >
            <SearchCreator
              placeholder={searchPlaceHolder}
              options={searchOptions}
              onChange={handleSearch}
            />
          </div>
        </div>
      )}
      {isMobileOrTablet && sortableFields.length > 0 && (
        <div data-cy={"sort-query"}>
          <div
            style={{
              marginBottom: "10px",
              marginRight: "-1rem",
              marginLeft: "-1rem",
            }}
          >
            <SortCreator options={sortableFields} onSort={handleSortSelect} />
          </div>
        </div>
      )}
      {isEmpty(listItems) ? (
        <NoDataFound
          headers={listHeaders}
          withActions={withActions}
          noDataText={noDataText}
        />
      ) : (
        <div style={{width: "100%"}}>
          {isMobileOrTablet ? (
            <CardList
              listHeaders={listHeaders}
              listItems={sortedData}
              withActions={withActions}
              {...rest}
            />
          ) : (
            <Table
              headers={listHeaders}
              data={sortedData}
              withActions={withActions}
              sortableFields={sortableFields}
              handleSort={handleSortClick}
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              {...rest}
            />
          )}
          {withPagination && totalCount > 25 && (
            <Pagination
              totalCount={totalCount}
              currentPage={withPagination.currentPage}
              onPageChange={(currentPage) => {
                withPagination.onPageChange(currentPage);
              }}
            />
          )}
        </div>
      )}
    </Fragment>
  );
};

ListView.propTypes = {
  /** Data which is need to be displayed */
  listItems: PropTypes.array.isRequired,
  /** List of headers to display on table */
  listHeaders: PropTypes.arrayOf(PropTypes.string).isRequired,
  /** Wether to display action column on table */
  withActions: PropTypes.bool,
  /** Wether to display search query component */
  withSearchQuery: PropTypes.bool,
  /** Placeholder for search query component */
  searchPlaceHolder: PropTypes.string,
  /** Options which will be passed to search query component */
  searchOptions: PropTypes.array,
  /** Callback function which will be executed after search query is created */
  handleSearch: PropTypes.func,
  /** Text to show no data is present */
  noDataText: PropTypes.string,
  /** Wether to display pagination for table */
  withPagination: PropTypes.bool,
  /** Total number of elements in data this is needed for pagination */
  totalCount: PropTypes.number,
  /** This prop will determine on the basis of which column rows are to be sorted.
   *  `type: "date"` sorts the column chronologically instead of alphabetically. */
  sortableFields: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
      type: PropTypes.oneOf(["date"]),
    })
  ),
};

export default memo(ListView);
